import {
  type FC,
  useCallback,
  useState,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import type { GenericOption } from 'pages/SmartConnectors/types';
import AxiosService from '__services/AxiosService';
import { useSelectTypeaheadWithScroll } from '__components/Inputs/Select/hooks';
import { searchEntityRecords } from '@kizen/api/records';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useSelector } from 'react-redux';
import { entityRecordsToOption } from 'ts-components/inputs/ValueInput/entityRecordsToOption';
import MultiSelect from '__components/Inputs/MultiSelect';
import { SmartConnectorContext } from '../../context';
import ApplySelectButton from '__components/Inputs/Select/ApplyButton';
import ClearSelectButton from '__components/Inputs/Select/ClearButton';

type CreateBulkactionModalProps = {
  onConfirm: (fields: GenericOption[]) => void;
  onHide: () => void;
};

export const CreateBulkactionModal: FC<CreateBulkactionModalProps> = ({
  onConfirm,
  onHide,
}) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState<GenericOption[]>([]);

  const { smartConnector } = useContext(SmartConnectorContext);

  const handleSubmit = useCallback(() => {
    onConfirm(fields);
  }, [onConfirm, fields]);

  const selectRef = useRef(null);

  const params = useMemo(
    () => ({
      active: true,
      custom_object_id: smartConnector.custom_object,
    }),
    [smartConnector.custom_object]
  );
  const contactObject = useSelector(getBusinessClientObject);

  const searchRecords = useCallback(
    ({ search, page }: { search: string; page: number }) => {
      return smartConnector.custom_object
        ? searchEntityRecords(AxiosService, smartConnector.custom_object, {
            params: {
              page_size: 20,
              page,
              search,
              ordering:
                smartConnector.custom_object === contactObject.id
                  ? 'full_name'
                  : 'name',
            },
          })
        : { count: 0, results: [], next: null, previous: null };
    },
    [smartConnector.custom_object, contactObject]
  );

  const [typeaheadProps, { loading }] = useSelectTypeaheadWithScroll({
    enabled: !!smartConnector.custom_object,
    fetch: searchRecords,
    selectRef,
    params,
    chosenValueIds: fields.map((field) => field.value),
    objectToOption: entityRecordsToOption(t),
    keepPreviousData: false,
  });

  return (
    <BasicModal
      size="medium"
      show
      onConfirm={handleSubmit}
      onHide={onHide}
      heading={t('Sample Bulk Action Payload')}
    >
      <MultiSelect
        ref={selectRef}
        value={fields}
        label={t('Sample Entity Records')}
        onChange={setFields}
        placeholder={t('Choose Records')}
        {...typeaheadProps}
        isLoading={loading}
        portal
        menuRightButton={<ApplySelectButton />}
        menuLeftButton={<ClearSelectButton />}
      />
    </BasicModal>
  );
};
